import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';

import { PORTAL_TOUR_FINISH_ROUTE, PORTAL_TOUR_STEPS, type PortalTourStep } from './portal-tour.steps';

const SEEN_KEY = 'es-portal-tour-seen';

/**
 * Guided first-run tour of the portal (Figma "Tutorial / Tour Callout", 8 steps).
 * Which step is showing, if any; the shell renders the callout and spotlight from it, and each
 * step that is explained on a particular screen takes the user there.
 */
@Injectable({ providedIn: 'root' })
export class PortalTourService {
  private readonly router = inject(Router);

  private readonly current = signal<number | null>(null);
  private readonly seenFlag = signal(readSeen());

  readonly steps = PORTAL_TOUR_STEPS;
  readonly total = PORTAL_TOUR_STEPS.length;

  /**
   * The 1-based step on screen, or null while no tour runs.
   */
  readonly step = this.current.asReadonly();

  /**
   * The content of the step on screen, or null while no tour runs.
   */
  readonly currentStep = computed<PortalTourStep | null>(() => {
    const step = this.current();
    return step === null ? null : (this.steps[step - 1] ?? null);
  });

  /**
   * True once the user finished or skipped the tour in this browser.
   */
  readonly seen = this.seenFlag.asReadonly();

  /**
   * Starts the tour from the first step.
   */
  start(): void {
    this.goTo(1);
  }

  /**
   * Moves on to the next step; past the last one the tour ends.
   */
  next(): void {
    const step = this.current();
    if (step === null) return;
    if (step >= this.total) this.stop();
    else this.goTo(step + 1);
  }

  /**
   * Steps back one; the first step stays put.
   */
  back(): void {
    const step = this.current();
    if (step === null || step <= 1) return;
    this.goTo(step - 1);
  }

  /**
   * Ends the tour, finished or skipped, and remembers it was seen.
   */
  stop(): void {
    this.current.set(null);
    this.markSeen();
  }

  /**
   * Ends the tour from its last step and takes the user to create their first island.
   */
  finish(): void {
    this.stop();
    this.navigate(PORTAL_TOUR_FINISH_ROUTE);
  }

  private goTo(step: number): void {
    this.current.set(step);
    const route = this.steps[step - 1]?.route;
    if (route && !this.router.url.startsWith(route)) this.navigate(route);
  }

  private navigate(url: string): void {
    this.router.navigateByUrl(url).catch(() => false);
  }

  private markSeen(): void {
    this.seenFlag.set(true);
    try {
      localStorage.setItem(SEEN_KEY, '1');
    } catch {
      // Storage blocked (private mode, sandbox): the flag lives for this session only.
    }
  }
}

function readSeen(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return false;
  }
}
